import React, { useCallback, useState } from 'react';
import {
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Edit } from '@mui/icons-material';
import { ILegalRequirementFullObject } from '../../../Interfaces/ILegalRequirement';
import { ConfirmDelete } from '../../../core/ConfirmDelete';
import { ICourtCourtRole } from '../../../Interfaces/IAemter';
import { EditLegalRequirement } from './EditLegalRequirement';
import { ILegalPhaseFullObject } from '../../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../../Interfaces/ILegalRequirementState';

interface IProps {
  currentRequirement: ILegalRequirementFullObject;
  currentPhase: ILegalPhaseFullObject;
  setLegalRequirementArray: Function;
  courtcourtroleArray: ICourtCourtRole[];
  legaltransactionId: number;
  selectedRequirementsIds: number[];
  setSelectedRequirementsIds: Function;
  legalPhaseArray: ILegalPhaseFullObject[];
  legalRequirementStates: ILegalRequirementState[];
}

export const LegalRequirementRowEdit: React.FC<IProps> = ({
  currentRequirement,
  currentPhase,
  setLegalRequirementArray,
  courtcourtroleArray,
  legaltransactionId,
  selectedRequirementsIds,
  setSelectedRequirementsIds,
  legalPhaseArray,
  legalRequirementStates,
}) => {
  const [isOpenEditLegalRequirement, setIsOpenEditLegalRequirement] = useState(
    currentRequirement.idLegalRequirement < 0 &&
      currentRequirement.LegalRequirement === ''
  );

  const handleCheckboxChange = () => {
    const id = currentRequirement.idLegalRequirement;
    const updatedSelected = selectedRequirementsIds.includes(id)
      ? selectedRequirementsIds.filter(reqId => reqId !== id)
      : [...selectedRequirementsIds, id];
    setSelectedRequirementsIds(updatedSelected);
  };

  const isFirstDisabled = () => {
    const currentIndex: number = currentPhase.LegalRequirementArray.map(
      x => x.idLegalRequirement
    ).indexOf(currentRequirement.idLegalRequirement);

    return currentIndex === 0;
  };

  const isLastDisabled = () => {
    const currentIndex: number = currentPhase.LegalRequirementArray.map(
      x => x.idLegalRequirement
    ).indexOf(currentRequirement.idLegalRequirement);

    return currentIndex === currentPhase.LegalRequirementArray.length - 1;
  };

  const isDeadlinePrequisiteAbove = useCallback(() => {
    const filteredRequirements =
      currentRequirement.DeadlineArray?.filter(x => {
        return (
          x.idLegalTransaction === legaltransactionId &&
          x.idLegalRequirement !== null &&
          currentPhase.LegalRequirementArray.some(
            y =>
              y.idLegalRequirement === x.idLegalRequirement &&
              y.Position === currentRequirement.Position - 1 &&
              y.idLegalPhase === currentRequirement.idLegalPhase
          )
        );
      }) || [];
    return filteredRequirements.length !== 0;
  }, [
    currentRequirement,
    currentPhase.LegalRequirementArray,
    legaltransactionId,
  ]);

  const isDeadlinePrequisiteBelow = useCallback(() => {
    const filteredRequirements =
      currentRequirement.DeadlineArray?.filter(x => {
        return (
          x.idLegalTransaction === legaltransactionId &&
          x.idLegalRequirement !== null &&
          currentPhase.LegalRequirementArray.some(
            y =>
              y.idLegalRequirement === x.idLegalRequirement &&
              y.Position === currentRequirement.Position + 1 &&
              y.idLegalPhase === currentRequirement.idLegalPhase
          )
        );
      }) || [];

    return filteredRequirements.length !== 0;
  }, [
    currentRequirement.DeadlineArray,
    currentRequirement.Position,
    currentRequirement.idLegalPhase,
    currentPhase.LegalRequirementArray,
    legaltransactionId,
  ]);

  const getUp = () => {
    const copyOfArray = [...currentPhase.LegalRequirementArray];
    const targetPositionn = currentRequirement.Position - 1;
    const toChangeObject = copyOfArray.find(
      x => x.Position === targetPositionn
    );
    const currentObject = { ...currentRequirement };

    if (toChangeObject !== undefined) {
      const currentIndex = currentPhase.LegalRequirementArray.map(
        x => x.idLegalRequirement
      ).indexOf(currentObject.idLegalRequirement);
      const targetIndex = currentPhase.LegalRequirementArray.map(
        x => x.idLegalRequirement
      ).indexOf(toChangeObject.idLegalRequirement);

      copyOfArray[currentIndex].Position =
        copyOfArray[currentIndex].Position - 1;
      copyOfArray[targetIndex].Position = copyOfArray[targetIndex].Position + 1;

      setLegalRequirementArray(copyOfArray);
    }
  };

  const getDown = () => {
    const copyOfArray = [...currentPhase.LegalRequirementArray];
    const targetPositionn = currentRequirement.Position + 1;
    const toChangeObject = copyOfArray.find(
      x => x.Position === targetPositionn
    );
    const currentObject = { ...currentRequirement };

    if (toChangeObject !== undefined) {
      const currentIndex = currentPhase.LegalRequirementArray.map(
        x => x.idLegalRequirement
      ).indexOf(currentObject.idLegalRequirement);
      const targetIndex = currentPhase.LegalRequirementArray.map(
        x => x.idLegalRequirement
      ).indexOf(toChangeObject.idLegalRequirement);

      copyOfArray[currentIndex].Position =
        copyOfArray[currentIndex].Position + 1;
      copyOfArray[targetIndex].Position = copyOfArray[targetIndex].Position - 1;

      setLegalRequirementArray(copyOfArray);
    }
  };

  const handleDelete = () => {
    setLegalRequirementArray([
      ...currentPhase.LegalRequirementArray.filter(
        x => x.idLegalRequirement !== currentRequirement.idLegalRequirement
      ),
    ]);
  };

  return (
    <>
      <EditLegalRequirement
        isOpenEditLegalRequirement={isOpenEditLegalRequirement}
        setIsOpenEditLegalRequirement={setIsOpenEditLegalRequirement}
        updateLegalRequirementArray={setLegalRequirementArray}
        currentPhase={currentPhase}
        courtcourtroleArray={courtcourtroleArray}
        legaltransactionId={legaltransactionId}
        legalRequirement={currentRequirement}
        currentLegalPhases={legalPhaseArray}
        legalRequirementStates={legalRequirementStates}
      ></EditLegalRequirement>

      <TableRow>
        <TableCell>
          <Checkbox
            checked={selectedRequirementsIds.includes(
              currentRequirement.idLegalRequirement
            )}
            onChange={handleCheckboxChange}
          />
        </TableCell>
        <TableCell>{currentRequirement.LegalRequirement}</TableCell>

        <TableCell></TableCell>
        <TableCell></TableCell>

        <TableCell>
          <Tooltip
            disableInteractive
            disableHoverListener={false}
            title={
              isLastDisabled()
                ? 'Die Maßnahme ist die letzte Maßnahme dieser Phase und kann nicht weiter nach unten verschoben werden.'
                : isDeadlinePrequisiteBelow()
                  ? 'Die Maßnahme unter dieser Maßnahme hat diese als Startbedinung, daher kann diese Maßnahme nicht weiter nach unten verschoben werden.'
                  : 'Verschiebt die Maßnahme eine Position nach unten.'
            }
          >
            <IconButton
              sx={{ float: 'right' }}
              size='small'
              disabled={isLastDisabled() || isDeadlinePrequisiteBelow()}
              onClick={getDown}
            >
              <ArrowDownward />
            </IconButton>
          </Tooltip>
          <Tooltip
            disableInteractive
            disableHoverListener={false}
            title={
              isFirstDisabled()
                ? 'Die Maßnahme ist die erste Maßnahme dieser Phase und kann nicht weiter nach oben verschoben werden.'
                : isDeadlinePrequisiteAbove()
                  ? 'Die Maßnahme hat eine Maßnahme als Startbedinung, die vor dieser Maßnahme liegt und kann daher nicht weiter nach oben verschoben werden.'
                  : 'Verschiebt die Maßnahme eine Position nach oben.'
            }
          >
            <IconButton
              sx={{ float: 'right' }}
              size='small'
              disabled={isFirstDisabled() || isDeadlinePrequisiteAbove()}
              onClick={getUp}
            >
              <ArrowUpward />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          {currentRequirement.isInterneLegalRequirement ? 'Ja' : 'Nein'}
        </TableCell>
        <TableCell>
          {currentRequirement.canUploadFilesClient ? 'Ja' : 'Nein'}
        </TableCell>
        <TableCell>
          {currentRequirement.canUploadFilesEmployees ? 'Ja' : 'Nein'}
        </TableCell>
        <TableCell>{currentRequirement.sendMail ? 'Ja' : 'Nein'}</TableCell>
        <TableCell>
          <IconButton
            size='small'
            onClick={() => {
              setIsOpenEditLegalRequirement(true);
            }}
          >
            <Edit />
          </IconButton>
          <ConfirmDelete
            itemText='die aktulle Bedingung'
            functionToDelete={handleDelete}
          />
        </TableCell>
      </TableRow>
    </>
  );
};
